/**
 * Schedule Section Component
 * Collapsible section for a single schedule in multi-schedule timeline
 */

import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { ChevronRight, ChevronDown, User } from 'lucide-react'

import {
    TimelineBarItem,
    TimelineDateRange,
    ScheduleGroup,
} from '../../../../types/timeline'
import { isToday } from '../utils/dateUtils'

interface ScheduleSectionProps {
    schedule: ScheduleGroup
    dates: dayjs.Dayjs[]
    dateRange: TimelineDateRange
    isExpanded: boolean
    onToggle: () => void
    onBarClick?: (item: TimelineBarItem, event: React.MouseEvent) => void
}

// Bar height and spacing
const BAR_HEIGHT = 28
const BAR_GAP = 4
const PADDING_Y = 6

interface BarWithRow extends TimelineBarItem {
    row: number
    startDay: number
    endDay: number
    startFraction: number // Hour fraction within start day (0-1)
    endFraction: number // Hour fraction within end day (0-1)
}

/**
 * Assign rows to overlapping bars
 */
const assignRowsToBars = (
    items: TimelineBarItem[],
    dateRange: TimelineDateRange
): BarWithRow[] => {
    const rangeStart = dayjs(dateRange.start).startOf('day')

    const itemsWithDays = items.map((item) => {
        const startTime = dayjs(item.value[1])
        const endTime = dayjs(item.value[2])
        const startDay = Math.max(
            0,
            startTime.startOf('day').diff(rangeStart, 'day')
        )

        // Calculate hour fractions within the day (0-1)
        const startHour = startTime.hour() + startTime.minute() / 60
        const endHour = endTime.hour() + endTime.minute() / 60
        const startFraction = startHour / 24
        // If end time is midnight (0:00), treat it as end of day (1.0)
        const endFraction = endHour === 0 ? 1 : endHour / 24

        // Check if shift crosses midnight to a different calendar day
        const crossesMidnight = !endTime.isSame(startTime, 'day')

        // Determine end day based on whether shift crosses midnight
        let endDay: number
        if (!crossesMidnight) {
            // Same calendar day - single day shift
            endDay = startDay
        } else {
            // Crosses midnight - calculate actual end day
            const adjustedEndTime = endTime.subtract(1, 'minute')
            endDay = Math.max(
                startDay,
                adjustedEndTime.startOf('day').diff(rangeStart, 'day')
            )
        }

        return { ...item, startDay, endDay, startFraction, endFraction, row: 0 }
    })

    // Sort by start time, then by duration (longer first)
    itemsWithDays.sort((a, b) => {
        const startA = a.value[1] as number
        const startB = b.value[1] as number
        if (startA !== startB) return startA - startB
        const durationA = (a.value[2] as number) - startA
        const durationB = (b.value[2] as number) - startB
        return durationB - durationA
    })

    // Assign rows using actual end timestamps for precise overlap detection
    const rowEndTimes: number[] = []
    itemsWithDays.forEach((item) => {
        const itemStartTime = item.value[1] as number

        let assignedRow = -1
        for (let r = 0; r < rowEndTimes.length; r++) {
            if (rowEndTimes[r] <= itemStartTime) {
                assignedRow = r
                break
            }
        }

        const itemEndTime = item.value[2] as number

        if (assignedRow === -1) {
            assignedRow = rowEndTimes.length
            rowEndTimes.push(itemEndTime)
        } else {
            rowEndTimes[assignedRow] = itemEndTime
        }
        item.row = assignedRow
    })

    return itemsWithDays
}

const ScheduleSection: React.FC<ScheduleSectionProps> = ({
    schedule,
    dates,
    dateRange,
    isExpanded,
    onToggle,
    onBarClick,
}) => {
    // Separate assignments by layer
    const { baseBars, overrideBars, baseRowCount, overrideRowCount } =
        useMemo(() => {
            const baseItems = schedule.assignments.filter(
                (a) => a.layer === 'base'
            )
            const overrideItems = schedule.assignments.filter(
                (a) => a.layer === 'override'
            )

            const baseBars = assignRowsToBars(baseItems, dateRange)
            const overrideBars = assignRowsToBars(overrideItems, dateRange)

            return {
                baseBars,
                overrideBars,
                baseRowCount:
                    baseBars.length > 0
                        ? Math.max(...baseBars.map((b) => b.row)) + 1
                        : 1,
                overrideRowCount:
                    overrideBars.length > 0
                        ? Math.max(...overrideBars.map((b) => b.row)) + 1
                        : 1,
            }
        }, [schedule.assignments, dateRange])

    // Calculate bar style with hour-level precision
    const getBarStyle = (item: BarWithRow, totalColumns: number) => {
        // Calculate percentage-based positioning with hour fractions
        const isSameDay = item.startDay === item.endDay

        let leftPercent: number
        let widthPercent: number

        if (isSameDay) {
            // Single day shift - position within the column based on hours
            leftPercent =
                ((item.startDay + item.startFraction) / totalColumns) * 100
            widthPercent =
                ((item.endFraction - item.startFraction) / totalColumns) * 100
        } else {
            // Multi-day shift
            leftPercent =
                ((item.startDay + item.startFraction) / totalColumns) * 100
            const totalDaySpan =
                item.endDay -
                item.startDay +
                item.endFraction -
                item.startFraction
            widthPercent = (totalDaySpan / totalColumns) * 100
        }

        // Minimum width for visibility (roughly 2 hours worth)
        const minWidthPercent = (2 / 24 / totalColumns) * 100

        return {
            left: `${leftPercent}%`,
            width: `${Math.max(widthPercent, minWidthPercent)}%`,
            top: PADDING_Y + item.row * (BAR_HEIGHT + BAR_GAP),
            backgroundColor: item.itemStyle.color,
        }
    }

    // Render bars for a layer
    const renderBars = (bars: BarWithRow[], rowCount: number) => {
        const contentHeight =
            PADDING_Y * 2 + rowCount * BAR_HEIGHT + (rowCount - 1) * BAR_GAP

        return (
            <div
                className="flex-1 relative"
                style={{ minHeight: Math.max(40, contentHeight) }}
            >
                {/* Background grid lines */}
                <div
                    className="absolute inset-0 grid"
                    style={{
                        gridTemplateColumns: `repeat(${dates.length}, minmax(40px, 1fr))`,
                    }}
                >
                    {dates.map((date, idx) => (
                        <div
                            key={idx}
                            className={`h-full border-r border-gray-100 last:border-r-0 ${isToday(date) ? 'bg-teal-50/50' : ''}`}
                        />
                    ))}
                </div>

                {/* Bars */}
                {bars.map((item, idx) => {
                    const style = getBarStyle(item, dates.length)
                    return (
                        <div
                            key={`${item.assignmentId}-${idx}`}
                            className="absolute rounded flex items-center gap-1 px-2 cursor-pointer hover:opacity-90 hover:shadow-md transition-all overflow-hidden"
                            style={{
                                left: style.left,
                                width: style.width,
                                top: style.top,
                                height: BAR_HEIGHT,
                                backgroundColor: style.backgroundColor,
                            }}
                            onClick={(e) => onBarClick?.(item, e)}
                        >
                            <div className="w-4 h-4 rounded-full bg-white/30 flex items-center justify-center flex-shrink-0">
                                <User className="w-2.5 h-2.5 text-white" />
                            </div>
                            <span className="text-white text-xs font-medium truncate">
                                {item.employeeName}
                            </span>
                        </div>
                    )
                })}

                {/* Empty state */}
                {bars.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs text-gray-300">-</span>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="border-b border-gray-200 last:border-b-0">
            {/* Schedule Header Row - Collapsed shows bars directly */}
            <div className="flex items-stretch bg-white hover:bg-gray-50 transition-colors">
                {/* Schedule name with toggle */}
                <div
                    className="w-40 px-3 py-2 flex items-center gap-2 cursor-pointer border-r border-gray-200 flex-shrink-0"
                    onClick={onToggle}
                >
                    {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-sm font-medium text-gray-700 truncate">
                        {schedule.scheduleName}
                    </span>
                </div>

                {/* Collapsed: Show base bars inline */}
                {!isExpanded && renderBars(baseBars, baseRowCount)}
            </div>

            {/* Expanded: Show both layers */}
            {isExpanded && (
                <>
                    {/* Scheduled layer */}
                    <div className="flex items-stretch border-t border-gray-100">
                        <div className="w-40 px-3 py-2 flex items-center border-r border-gray-200 bg-gray-50 flex-shrink-0">
                            <span className="text-xs text-gray-500 ml-6">
                                Scheduled
                            </span>
                        </div>
                        {renderBars(baseBars, baseRowCount)}
                    </div>

                    {/* Swap layer */}
                    <div className="flex items-stretch border-t border-gray-100">
                        <div className="w-40 px-3 py-2 flex items-center border-r border-gray-200 bg-gray-50 flex-shrink-0">
                            <span className="text-xs text-gray-500 ml-6">
                                Swap
                            </span>
                        </div>
                        {renderBars(overrideBars, overrideRowCount)}
                    </div>
                </>
            )}
        </div>
    )
}

export default ScheduleSection
